import IPostMessage from '../../IPostMessage';
import validate from '../../Validate/Validate';
import IPackage from './IPackage';

/**
 * The `sos.management.package` API groups together methods for installing custom Android packages.
 *
 * <details>
 *     <summary>Package Management Capabilities</summary>
 * 		| Capability | Description |
 * 		|:------------|:-------------|
 * 		| `PACKAGE_INSTALL` | If device supports installing packages |
 * 		| `STOP_PACKAGE` | If device supports stopping packages |
 *
 * 		If you want to check if the device supports those capabilities, use [`sos.management.supports()`](https://developers.signageos.io/sdk/sos_management/#supports).
 * </details>
 */
export default class Package implements IPackage {
	/** @internal */
	constructor(
		private messagePrefix: string,
		private postMessage: IPostMessage<any>,
	) {}

	/**
	 * The `install()` method installs a particular package from the specified URL.
	 *
	 * @param baseUrl URL where the package is available
	 * @param packageName Name of the Android package
	 * @param version Package version
	 * @param build If relevant for your device
	 * @returns {Promise<void>} A promise that resolves when the package is successfully installed.
	 * @throws {Error} If `baseUrl` is not a valid URL
	 * @throws {Error} If `packageName` is not a string
	 * @throws {Error} If `version` is not a string
	 * @throws {Error} If `build` is not a string or null
	 */
	public async install(baseUrl: string, packageName: string, version: string, build: string | null): Promise<void> {
		validate({ baseUrl }).required().uri();
		validate({ packageName }).required().string();
		validate({ version }).required().string();
		validate({ build }).canBeNull().required().string();
		await this.postMessage({
			type: this.getMessage('install_package'),
			baseUrl,
			packageName,
			version,
			build,
		});
	}

	/**
	 * The `stop()` method stops a certain package.
	 *
	 * @param packageName Name of the android package
	 *
	 * @returns {Promise<boolean>} Resolves to `true` if the package was stopped, `false` if the package was already stopped before.
	 * @since 8.2.0
	 */
	public async stop(packageName: string): Promise<boolean> {
		validate({ packageName }).required().string();
		const { stopped } = await this.postMessage({
			type: this.getMessage('stop_package'),
			packageName,
		});

		return stopped;
	}

	/**
	 * The `clearData()` method stops a certain package and clears its user data.
	 *
	 * @param packageName Name of the android package
	 *
	 * @returns {Promise<void>} Resolves if the package data was cleared.
	 * @since 8.8.0
	 */
	public async clearData(packageName: string): Promise<void> {
		validate({ packageName }).required().string();
		await this.postMessage({
			type: this.getMessage('clear_package_data'),
			packageName,
		});
	}

	private getMessage(name: string) {
		return this.messagePrefix + '.' + name;
	}
}
